<script setup lang="ts">
import type { ActivityListProps } from '../../../model/assessments/activity-list.hook'

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import ActivityEditor from './ActivityEditor.vue'
import { useActivityList } from '../../../model/assessments/activity-list.hook'

defineOptions({
  name: 'ActivityList',
})

const props = defineProps<ActivityListProps>()

const {
  refActivities,
  refHasActivities,
  refSelectedActivityId,
  selectActivity,
  addActivity,
  removeActivity,
  moveActivityUp,
  moveActivityDown,
} = useActivityList({
  screenId: () => props.screenId,
})
</script>

<template>
  <div class="activity-list">
    <AppText name="body1-medium">
      Activities
    </AppText>

    <!-- A screen with no activities is LEGITIMATE, not broken: the schema's own comment calls it
         a passive screen (an intro, or a passage on its own), and refinement 7 only asks for
         content or activities. The copy says so rather than reading like an error. -->
    <AppText
      v-if="!refHasActivities"
      name="body2"
      color="slate-500"
    >
      No activities on this screen. That is fine — a screen with content but no activities is a
      passive screen, such as an intro or a passage the kid only reads. Add an activity if the kid
      should respond here.
    </AppText>

    <ul class="activity-list__items">
      <li
        v-for="activity in refActivities"
        :key="activity.id"
        :class="['activity-list__item', { 'is-selected': activity.isSelected }]"
      >
        <!-- Title on the left, the three controls pinned to the top right: they act on the
             activity as a whole, so they belong beside its name rather than under the text
             that describes it. -->
        <div class="activity-list__head">
          <button
            type="button"
            class="activity-list__select"
            @click="selectActivity(activity.id)"
          >
            <AppText
              name="body2-medium"
              truncate
            >
              {{ activity.label }}
            </AppText>
            <AppText
              name="body2"
              color="slate-500"
              truncate
            >
              {{ activity.metaLabel }}
            </AppText>
          </button>
          <div class="activity-list__actions">
            <BtnIcon
              :icon="faArrowUp"
              size="small"
              title="Move activity up"
              :disabled="activity.isFirstInOrder"
              @click="moveActivityUp(activity.id)"
            />
            <BtnIcon
              :icon="faArrowDown"
              size="small"
              title="Move activity down"
              :disabled="activity.isLastInOrder"
              @click="moveActivityDown(activity.id)"
            />
            <BtnIcon
              :icon="faTrashCan"
              size="small"
              color="red"
              title="Remove activity"
              @click="removeActivity(activity.id)"
            />
          </div>
        </div>

        <ActivityEditor
          v-if="activity.isSelected && refSelectedActivityId"
          :screen-id="screenId"
          :activity-id="refSelectedActivityId"
        />
      </li>
    </ul>

    <BtnSecondary
      text="Add activity"
      element-width="full"
      @click="addActivity"
    />
  </div>
</template>

<style scoped lang="scss" src="./ActivityList.scss" />
